import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import Link from "next/link";
import { Publication } from "@/types/types";
import { useTranslations } from "next-intl";
import { IoOptionsOutline, IoStatsChart } from "react-icons/io5";

interface PublicationsListProps {
  publications: Publication[];
  authors: Set<string>;
  types: Set<string>;
  journals: Set<string>;
}

type SortOption = "date" | "ranking" | "citations";

const rankOrder = ["D1", "Q1", "Q2", "Q3", "Q4", "N/A"];
const norwayLevels = ["2", "1", "0", "N/A"];

const PublicationsList: React.FC<PublicationsListProps> = ({
  publications,
  authors,
  types,
  journals,
}) => {
  const t = useTranslations("publications");
  const typeT = useTranslations("statistics.publicationTypes");

  const publicationTypeTranslations = {
    journalArticle: typeT("journalArticle"),
    conferenceArticle: typeT("conferenceArticle"),
    bookChapter: typeT("bookChapter"),
    book: typeT("book"),
    patent: typeT("patent"),
    thesis: typeT("thesis"),
    other: typeT("other")
  };

  const [sortOption, setSortOption] = useState<SortOption>("date");
  const [isReversed, setIsReversed] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedJournal, setSelectedJournal] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [selectedRankings, setSelectedRankings] = useState<string[]>([]);
  const [selectedNorwayLevels, setSelectedNorwayLevels] = useState<string[]>([]);
  const [showRankingDropdown, setShowRankingDropdown] = useState(false);
  const [showNorwayDropdown, setShowNorwayDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const norwayDropdownRef = useRef<HTMLDivElement>(null);

  const sortedAndFilteredPublications = useMemo(() => {
    let sorted = [...publications];

    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      sorted = sorted.filter(
        (pub) =>
          pub.title?.toLowerCase().includes(searchLower) ||
          pub.journal?.label?.toLowerCase().includes(searchLower) ||
          pub.authorships?.some((a: any) =>
            (a.author?.label || `${a.familyName} ${a.givenName}`)
              .toLowerCase()
              .includes(searchLower)
          ) ||
          pub.mtid?.toString().toLowerCase().includes(searchLower)
      );
    }

    if (selectedAuthor) {
      sorted = sorted.filter((pub) =>
        pub.authorships?.some(
          (a: any) =>
            a.author?.label === selectedAuthor ||
            `${a.familyName} ${a.givenName}` === selectedAuthor
        )
      );
    }

    if (selectedType) {
      sorted = sorted.filter((pub) => {
        const publicationType = pub.getPublicationType(publicationTypeTranslations).toLowerCase();
        return publicationType === selectedType.toLowerCase();
      });
    }

    if (selectedJournal) {
      sorted = sorted.filter((pub) => pub.journal?.label === selectedJournal);
    }

    if (selectedRankings.length > 0) {
      sorted = sorted.filter((pub) =>
        selectedRankings.includes(pub.getRating())
      );
    }

    if (selectedNorwayLevels.length > 0) {
      sorted = sorted.filter((pub) => {
        const level = pub.norwayLevel?.toString() ?? "N/A";
        return selectedNorwayLevels.includes(level);
      });
    }

    sorted.sort((a, b) => {
      switch (sortOption) {
        case "date":
          return (b.publishedYear ?? 0) - (a.publishedYear ?? 0) || 0;
        case "ranking": {
          const ratingA = a.getRating();
          const ratingB = b.getRating();
          if (ratingA === "N/A" && ratingB === "N/A") return 0;
          if (ratingA === "N/A") return 1;
          if (ratingB === "N/A") return -1;
          return rankOrder.indexOf(ratingA) - rankOrder.indexOf(ratingB);
        }
        case "citations":
          return (b.citationCount ?? 0) - (a.citationCount ?? 0) || 0;
        default:
          return 0;
      }
    });

    return isReversed ? sorted.reverse() : sorted;
  }, [
    publications,
    sortOption,
    isReversed,
    selectedAuthor,
    selectedType,
    selectedJournal,
    searchText,
    selectedRankings,
    selectedNorwayLevels,
  ]);

  const publicationCount = sortedAndFilteredPublications.length;

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSortOption(e.target.value as SortOption);
    },
    []
  );

  const handleAuthorChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedAuthor(e.target.value);
    },
    []
  );

  const handleTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedType(e.target.value);
    },
    []
  );

  const toggleReverse = useCallback(() => {
    setIsReversed((prev) => !prev);
  }, []);

  const handleJournalChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedJournal(e.target.value);
    },
    []
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    []
  );

  const handleRankingChange = (ranking: string) => {
    setSelectedRankings((prevSelected) => {
      if (prevSelected.includes(ranking)) {
        return prevSelected.filter((r) => r !== ranking);
      } else {
        return [...prevSelected, ranking];
      }
    });
  };

  const handleNorwayLevelChange = (level: string) => {
    setSelectedNorwayLevels((prevSelected) => {
      if (prevSelected.includes(level)) {
        return prevSelected.filter((r) => r !== level);
      } else {
        return [...prevSelected, level];
      }
    });
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowRankingDropdown(false);
    }
    if (
      norwayDropdownRef.current &&
      !norwayDropdownRef.current.contains(event.target as Node)
    ) {
      setShowNorwayDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!publications || publications.length === 0) return null;

  return (
    <div className="bg-white/60 backdrop-blur-xl shadow-xl rounded-2xl border border-white/20 mt-10">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 p-3 sm:p-4 bg-white/40 border-b border-white/30 rounded-t-2xl">
        {t("title")}
      </h2>

      <div className="p-4 sm:p-6 bg-white/30 space-y-4 rounded-b-2xl">
        {/* Search */}
        <div className="w-full">
          <label
            htmlFor="search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {t("search.label")}
          </label>
          <input
            type="text"
            id="search"
            className="w-full p-2 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70 "
            placeholder={t("search.placeholder")}
            value={searchText}
            onChange={handleSearchChange}
          />
        </div>

        {/* Simple controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="sortOption" className="block text-sm font-medium text-gray-700 mb-1">
              {t("filters.sort.label")}
            </label>
            <select
              id="sortOption"
              className="w-full p-2 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70 "
              value={sortOption}
              onChange={handleSortChange}
            >
              <option value="date">{t("filters.sort.options.date")}</option>
              <option value="ranking">{t("filters.sort.options.ranking")}</option>
              <option value="citations">{t("filters.sort.options.citations")}</option>
            </select>
          </div>
          <div className="flex items-end justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="reverseOrder"
                checked={isReversed}
                onChange={toggleReverse}
                className="h-4 w-4 text-bme-main focus:ring-bme-main border-white/40 rounded"
              />
              <label htmlFor="reverseOrder" className="ml-2 text-sm text-gray-900">
                {t("controls.reverseOrder")}
              </label>
            </div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="group inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-300/60 text-slate-700 bg-slate-100/60 hover:bg-slate-200/70 hover:border-slate-400/70 transition focus:outline-none focus:ring-2 focus:ring-slate-300/60 cursor-pointer"
              aria-pressed={showAdvanced}
            >
              <IoOptionsOutline className="text-slate-600 group-hover:text-slate-700 transition-transform group-hover:scale-105" />
              <span>{showAdvanced ? t('filters.hideAdvanced') : t('filters.showAdvanced')}</span>
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Author Filter */}
            <div>
              <label htmlFor="authorFilter" className="block text-sm font-medium text-gray-700 mb-1">
                {t("filters.author.label")}
              </label>
              <select
                id="authorFilter"
                className="w-full p-2 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70 "
                value={selectedAuthor}
                onChange={handleAuthorChange}
              >
                <option value="">{t("filters.author.all")}</option>
                {Array.from(authors).map((author) => (
                  <option key={author} value={author}>
                    {author}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <label htmlFor="typeFilter" className="block text-sm font-medium text-gray-700 mb-1">
                {t("filters.type.label")}
              </label>
              <select
                id="typeFilter"
                className="w-full p-2 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70 "
                value={selectedType}
                onChange={handleTypeChange}
              >
                <option value="">{t("filters.type.all")}</option>
                {Array.from(types).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Journal Filter */}
            <div>
              <label htmlFor="journalFilter" className="block text-sm font-medium text-gray-700 mb-1">
                {t("filters.journal.label")}
              </label>
              <select
                id="journalFilter"
                className="w-full p-2 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70 "
                value={selectedJournal}
                onChange={handleJournalChange}
              >
                <option value="">{t("filters.journal.all")}</option>
                {Array.from(journals).map((journal) => (
                  <option key={journal} value={journal}>
                    {journal}
                  </option>
                ))}
              </select>
            </div>

            {/* Ranking Filter */}
            <div className="relative">
              <label htmlFor="rankingFilter" className="block text-sm font-medium text-gray-700 mb-1">
                {t("filters.ranking.label")}
              </label>
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={
                      selectedRankings.length > 0
                        ? selectedRankings.join(", ")
                        : t("filters.ranking.noSelection")
                    }
                    className="w-full h-10 px-2 pr-8 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70  cursor-pointer"
                    onClick={() => setShowRankingDropdown(!showRankingDropdown)}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-1 pointer-events-none">
                    <svg className="h-4 w-4 text-gray-900" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                {showRankingDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white/95 backdrop-blur rounded-lg shadow-xl p-1 z-[100] border border-white/40">
                    {rankOrder.map((ranking) => (
                      <div
                        key={ranking}
                        className={`p-2 hover:bg-bme-main/10 cursor-pointer rounded-md transition-colors ${
                          selectedRankings.includes(ranking) ? "bg-bme-main/20" : ""
                        }`}
                        onClick={() => {
                          handleRankingChange(ranking);
                        }}
                      >
                        {ranking}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Norway Filter */}
            <div className="relative">
              <label htmlFor="norwayFilter" className="block text-sm font-medium text-gray-700 mb-1">
                {t("filters.norway.label")}
              </label>
              <div className="relative" ref={norwayDropdownRef}>
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={
                      selectedNorwayLevels.length > 0
                        ? selectedNorwayLevels.join(", ")
                        : t("filters.norway.noSelection")
                    }
                    className="w-full h-10 px-2 pr-8 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70  cursor-pointer"
                    onClick={() => setShowNorwayDropdown(!showNorwayDropdown)}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-1 pointer-events-none">
                    <svg className="h-4 w-4 text-gray-900" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                {showNorwayDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white/95 backdrop-blur rounded-lg shadow-xl p-1 z-[100] border border-white/40">
                    {norwayLevels.map((level) => (
                      <div
                        key={level}
                        className={`p-2 hover:bg-bme-main/10 cursor-pointer rounded-md transition-colors ${
                          selectedNorwayLevels.includes(level) ? "bg-bme-main/20" : ""
                        }`}
                        onClick={() => {
                          handleNorwayLevelChange(level);
                        }}
                      >
                        {level}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {t("controls.displayCount")}: {publicationCount}
          </div>
        </div>
      </div>

      {/* Publications List */}
      <ul className="divide-y divide-white/40 mt-4">
        {sortedAndFilteredPublications.map((pub, index) => (
          <li key={index} className="p-3 sm:p-4 hover:bg-white/60 transition duration-150 ease-in-out">
            <div className="flex flex-col gap-1.5">
              {/* Top row: ranking + title, and citations on the right */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  <span className={`${pub.getRatingColor()} px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-semibold rounded-lg text-white shadow flex-shrink-0`}>
                    {pub.getRating()}
                  </span>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug break-words line-clamp-2">
                    {pub.identifiers && pub.identifiers.length > 0 && pub.identifiers[0].realUrl ? (
                      <Link
                        href={pub.identifiers[0].realUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 hover:underline decoration-bme-main/40"
                      >
                        {pub.title}
                      </Link>
                    ) : (
                      <span className="text-gray-900">{pub.title}</span>
                    )}
                  </h3>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-indigo-700 px-2.5 py-1 bg-indigo-50/70 border border-indigo-200/70 rounded-lg shadow-sm flex-shrink-0">
                  <IoStatsChart className="text-indigo-600" />
                  {t("publication.citations")}: {pub.citationCount ?? 0}
                </span>
              </div>

              {/* Meta line */}
              <p className="text-xs sm:text-sm text-gray-600 line-clamp-1">
                {pub.journal?.label || ''} {pub.journal && pub.publishedYear ? '·' : ''} {pub.publishedYear ?? t("publication.unknownYear")}
              </p>

              {/* Authors */}
              <p className="text-[11px] sm:text-xs text-gray-600 line-clamp-1">
                {pub.authorships?.map((a: any, i: number) => (
                  <React.Fragment key={a.author?.label || `${a.familyName} ${a.givenName}`}>
                    {i > 0 && ", "}
                    {a.author?.mtid ? (
                      <Link
                        href={`/${encodeURIComponent(a.author.mtid)}`}
                        className="text-gray-700 hover:underline decoration-bme-main/40"
                      >
                        {a.author.label || `${a.familyName} ${a.givenName}`}
                      </Link>
                    ) : (
                      <span className="text-gray-700">{a.author?.label || `${a.familyName} ${a.givenName}`}</span>
                    )}
                  </React.Fragment>
                ))}
              </p>

              {/* Badges row */}
              <div className="flex flex-wrap gap-2 pt-1">
                <span className="px-2 py-1 bg-white/70 border border-white/40 text-gray-700 text-xs rounded-lg shadow-sm">
                  {pub.getPublicationType(publicationTypeTranslations)}
                </span>
                <span className="px-2 py-1 bg-white/70 border border-white/40 text-gray-700 text-xs rounded-lg shadow-sm">
                  {pub.isScientific() ? t("publication.scientific") : t("publication.nonScientific")}
                </span>
                {typeof pub.norwayLevel === 'number' && (
                  <span className="px-2 py-1 bg-white/70 border border-white/40 text-gray-700 text-xs rounded-lg shadow-sm">
                    {t("publication.norwayLevel")}: {pub.norwayLevel}
                  </span>
                )}
                {pub.hasISBNISSN === false && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-lg border border-red-200">
                    {t("publication.noIdentifier")}
                  </span>
                )}
                {pub.mtid && (
                  <span className="px-2 py-1 bg-white/70 border border-white/40 text-gray-500 text-xs rounded-lg">
                    {t("publication.mtmtId")}: {pub.mtid}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PublicationsList;
